import { Controller } from "@hotwired/stimulus"

// Controller für Profile-Locking-Mechanismus
// Verhindert gleichzeitige Bearbeitung durch mehrere Benutzer
export default class extends Controller {
  static values = {
    profileId: Number,
    lockStatusUrl: String,
    unlockUrl: String
  }

  static targets = ["warning", "autosaveNotice", "form"]

  connect() {
    if (!this.profileIdValue) return

    // Setze Lock beim Öffnen der Seite
    this.checkLockStatus()
    
    // Prüfe alle 10 Sekunden ob Lock noch aktiv ist
    this.lockCheckInterval = setInterval(() => {
      this.checkLockStatus()
    }, 10000)

    // Verlängere Lock alle 2 Minuten
    this.lockExtendInterval = setInterval(() => {
      this.extendLock()
    }, 120000)

    // Entferne Lock beim Verlassen der Seite
    window.addEventListener('beforeunload', this.unlockOnLeave.bind(this))
    document.addEventListener('turbo:before-visit', this.unlockOnLeave.bind(this))
  }

  disconnect() {
    // Entferne Lock beim Verlassen
    this.unlockOnLeave()
    
    // Stoppe Intervalle
    if (this.lockCheckInterval) {
      clearInterval(this.lockCheckInterval)
    }
    if (this.lockExtendInterval) {
      clearInterval(this.lockExtendInterval)
    }

    // Entferne Event Listener
    window.removeEventListener('beforeunload', this.unlockOnLeave.bind(this))
    document.removeEventListener('turbo:before-visit', this.unlockOnLeave.bind(this))
  }

  checkLockStatus() {
    if (!this.lockStatusUrlValue) return

    fetch(this.lockStatusUrlValue, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(data => {
      if (data.locked && data.locked_by) {
        // Profil wird von anderem Benutzer bearbeitet
        this.showWarning(data.locked_by)
        this.disableAutosave()
      } else {
        // Profil ist nicht gesperrt oder wir sind der Lock-Inhaber
        this.hideWarning()
        this.enableAutosave()
      }
    })
    .catch(error => {
      console.error('Fehler beim Prüfen des Lock-Status:', error)
    })
  }

  extendLock() {
    if (!this.lockStatusUrlValue) return

    // Lock wird automatisch beim Update verlängert
    // Diese Methode wird hauptsächlich für die visuelle Bestätigung verwendet
    this.checkLockStatus()
  }

  unlockOnLeave() {
    if (!this.unlockUrlValue) return

    // Verwende sendBeacon für zuverlässiges Senden beim Verlassen
    const formData = new FormData()
    formData.append('_method', 'DELETE')

    if (navigator.sendBeacon) {
      navigator.sendBeacon(this.unlockUrlValue, formData)
    } else {
      // Fallback für ältere Browser
      fetch(this.unlockUrlValue, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        credentials: 'same-origin',
        keepalive: true
      }).catch(() => {
        // Ignoriere Fehler beim Verlassen
      })
    }
  }

  dismissWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.style.display = 'none'
    }
  }

  showWarning(lockedBy) {
    if (this.hasWarningTarget) {
      this.warningTarget.style.display = 'block'
      
      // Aktualisiere den Text mit dem Namen des Benutzers
      const nameElement = this.warningTarget.querySelector('.text-yellow-700')
      if (nameElement && lockedBy.user_name) {
        nameElement.textContent = `${lockedBy.user_name} bearbeitet dieses Profil. Ihre Änderungen werden nicht automatisch gespeichert.`
      }
    }
  }

  hideWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.style.display = 'none'
    }
  }

  disableAutosave() {
    // Deaktiviere Autosave wenn anderer Benutzer das Profil bearbeitet
    if (this.hasAutosaveNoticeTarget) {
      this.autosaveNoticeTarget.style.opacity = '0.5'
    }
    
    // Finde den Autosave-Controller und deaktiviere ihn
    const autosaveController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'autosave'
    )
    
    if (autosaveController && typeof autosaveController.disable === 'function') {
      autosaveController.disable()
    }
  }

  enableAutosave() {
    // Aktiviere Autosave wieder wenn wir der Lock-Inhaber sind
    if (this.hasAutosaveNoticeTarget) {
      this.autosaveNoticeTarget.style.opacity = '1'
    }
    
    // Finde den Autosave-Controller und aktiviere ihn
    const autosaveController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'autosave'
    )
    
    if (autosaveController && typeof autosaveController.enable === 'function') {
      autosaveController.enable()
    }
  }
}

